package com.videoanalyzer.web.components.auth

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Legal pages: Terms of Service, Privacy Policy, and Contact.
 */

@Composable
fun TermsOfServicePage(onBack: () -> Unit) {
    LegalPageLayout(
        title = "Terms of Service",
        onBack = onBack
    ) {
        LegalSection("1. Acceptance of Terms") {
            Text("By accessing and using VideoScore (\"the Service\"), operated by Mayur Software Services, registered in the Netherlands, you accept and agree to be bound by the terms and conditions of this agreement. If you do not agree to these terms, please do not use the Service.")
        }

        LegalSection("2. Description of Service") {
            Text("VideoScore provides YouTube video analysis tools that help content creators optimize their videos for better performance. The Service analyzes video metadata, thumbnails, titles, descriptions, and other publicly available information to provide actionable insights and recommendations.")
        }

        LegalSection("3. User Accounts") {
            P { Text("To use certain features of the Service, you must create an account. You agree to:") }
            Ul({
                style {
                    marginTop(12.px)
                    paddingLeft(24.px)
                    color(Color("#94a3b8"))
                }
            }) {
                Li { Text("Provide accurate, current, and complete information during registration") }
                Li { Text("Maintain and promptly update your account information") }
                Li { Text("Maintain the security of your password and accept all risks of unauthorized access") }
                Li { Text("Notify us immediately if you discover any unauthorized use of your account") }
            }
        }

        LegalSection("4. User Conduct") {
            P { Text("You agree not to:") }
            Ul({
                style {
                    marginTop(12.px)
                    paddingLeft(24.px)
                    color(Color("#94a3b8"))
                }
            }) {
                Li { Text("Use the Service for any unlawful purpose or in violation of any laws") }
                Li { Text("Attempt to gain unauthorized access to the Service or its related systems") }
                Li { Text("Interfere with or disrupt the integrity or performance of the Service") }
                Li { Text("Use automated scripts to collect information from the Service") }
                Li { Text("Impersonate any person or entity or misrepresent your affiliation") }
            }
        }

        LegalSection("5. Intellectual Property") {
            Text("The Service and its original content, features, and functionality are owned by Mayur Software Services and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works based on the Service without our express written permission.")
        }

        LegalSection("6. Third-Party Services") {
            Text("The Service integrates with YouTube and other third-party services. Your use of these services is subject to their respective terms of service and privacy policies. We are not responsible for the content, privacy policies, or practices of any third-party services.")
        }

        LegalSection("7. Disclaimer of Warranties") {
            Text("THE SERVICE IS PROVIDED \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK.")
        }

        LegalSection("8. Limitation of Liability") {
            Text("IN NO EVENT SHALL MAYUR SOFTWARE SERVICES OR VIDEOSCORE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF THE SERVICE.")
        }

        LegalSection("9. Changes to Terms") {
            Text("We reserve the right to modify these terms at any time. We will provide notice of significant changes by posting the new terms on the Service. Your continued use of the Service after such modifications constitutes your acceptance of the updated terms.")
        }

        LegalSection("10. Contact Information") {
            Text("If you have any questions about these Terms of Service, please contact us at support@videoscore.io")
        }

        P({
            style {
                marginTop(32.px)
                fontSize(13.px)
                color(Color("#64748b"))
                fontStyle("italic")
            }
        }) {
            Text("Last updated: March 2026")
        }
    }
}

@Composable
fun PrivacyPolicyPage(onBack: () -> Unit) {
    LegalPageLayout(
        title = "Privacy Policy",
        onBack = onBack
    ) {
        P({
            style {
                marginBottom(24.px)
                fontSize(14.px)
                color(Color("#94a3b8"))
                lineHeight("1.6")
            }
        }) {
            Text("VideoScore is operated by Mayur Software Services, registered in the Netherlands. This Privacy Policy explains how we collect, use, and protect your personal information.")
        }

        LegalSection("1. Information We Collect") {
            P { Text("We collect information you provide directly to us, including:") }
            Ul({
                style {
                    marginTop(12.px)
                    paddingLeft(24.px)
                    color(Color("#94a3b8"))
                }
            }) {
                Li { Text("Account information (email address, name) when you register") }
                Li { Text("YouTube video URLs you submit for analysis") }
                Li { Text("Usage data about how you interact with our Service") }
                Li { Text("Device information and browser type") }
            }
        }

        LegalSection("2. How We Use Your Information & Legal Basis") {
            P { Text("We use the information we collect to:") }
            Ul({
                style {
                    marginTop(12.px)
                    paddingLeft(24.px)
                    color(Color("#94a3b8"))
                }
            }) {
                Li { Text("Provide, maintain, and improve our Service") }
                Li { Text("Process and complete video analysis requests") }
                Li { Text("Send you technical notices, updates, and support messages") }
                Li { Text("Respond to your comments, questions, and requests") }
                Li { Text("Monitor and analyze trends, usage, and activities") }
                Li { Text("Detect, investigate, and prevent fraudulent transactions and abuse") }
            }

            P({
                style {
                    marginTop(16.px)
                    fontWeight(600)
                    color(Color("#f8fafc"))
                }
            }) { Text("Legal Basis for Processing (GDPR)") }
            Ul({
                style {
                    marginTop(8.px)
                    paddingLeft(24.px)
                    color(Color("#94a3b8"))
                }
            }) {
                Li { Text("Contract: To provide our video analysis service to you") }
                Li { Text("Consent: For optional analytics and marketing communications") }
                Li { Text("Legitimate Interest: To improve our service and prevent fraud") }
                Li { Text("Legal Obligation: To comply with applicable laws") }
            }
        }

        LegalSection("3. Information Sharing") {
            P { Text("We do not sell, trade, or otherwise transfer your personal information to third parties except:") }
            Ul({
                style {
                    marginTop(12.px)
                    paddingLeft(24.px)
                    color(Color("#94a3b8"))
                }
            }) {
                Li { Text("With service providers who assist in operating our Service") }
                Li { Text("To comply with legal obligations or respond to lawful requests") }
                Li { Text("To protect the rights, property, or safety of VideoScore and our users") }
                Li { Text("In connection with a merger, acquisition, or sale of assets") }
            }
        }

        LegalSection("4. Third-Party Services") {
            P { Text("Our Service integrates with third-party services:") }
            Ul({
                style {
                    marginTop(12.px)
                    paddingLeft(24.px)
                    color(Color("#94a3b8"))
                }
            }) {
                Li { Text("YouTube Data API: To fetch publicly available video metadata") }
                Li { Text("Google Sign-In: For authentication purposes") }
                Li { Text("OpenAI: To power our AI-driven analysis features") }
            }
            P({
                style { marginTop(12.px) }
            }) {
                Text("Each third-party service has its own privacy policy governing the use of your information.")
            }
        }

        LegalSection("5. Data Security") {
            Text("We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.")
        }

        LegalSection("6. Data Retention") {
            Text("We retain your personal information for as long as your account is active or as needed to provide you services. We will retain and use your information as necessary to comply with legal obligations, resolve disputes, and enforce our agreements.")
        }

        LegalSection("7. Your Rights") {
            P { Text("You have the right to:") }
            Ul({
                style {
                    marginTop(12.px)
                    paddingLeft(24.px)
                    color(Color("#94a3b8"))
                }
            }) {
                Li { Text("Access and receive a copy of your personal data") }
                Li { Text("Rectify inaccurate personal data") }
                Li { Text("Request deletion of your personal data") }
                Li { Text("Object to or restrict processing of your personal data") }
                Li { Text("Data portability") }
            }
            P({
                style { marginTop(12.px) }
            }) {
                Text("To exercise these rights, please contact us at support@videoscore.io")
            }
        }

        LegalSection("8. Cookies and Tracking") {
            P { Text("We use cookies and similar technologies. Here are the cookies we use:") }

            P({
                style {
                    marginTop(16.px)
                    fontWeight(600)
                    color(Color("#f8fafc"))
                }
            }) { Text("Essential Cookies (Required)") }
            Ul({
                style {
                    marginTop(8.px)
                    paddingLeft(24.px)
                    color(Color("#94a3b8"))
                }
            }) {
                Li { Text("Authentication tokens: To keep you logged in securely") }
                Li { Text("Cookie consent preference: To remember your cookie choices") }
            }

            P({
                style {
                    marginTop(16.px)
                    fontWeight(600)
                    color(Color("#f8fafc"))
                }
            }) { Text("Analytics Cookies (Optional)") }
            Ul({
                style {
                    marginTop(8.px)
                    paddingLeft(24.px)
                    color(Color("#94a3b8"))
                }
            }) {
                Li { Text("Usage analytics: To understand how you use our service and improve it") }
            }

            P({
                style { marginTop(12.px) }
            }) {
                Text("You can manage your cookie preferences at any time. Essential cookies cannot be disabled as they are necessary for the service to function.")
            }
        }

        LegalSection("9. Children's Privacy") {
            Text("Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us.")
        }

        LegalSection("10. Changes to This Privacy Policy") {
            Text("We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the \"Last updated\" date. You are advised to review this Privacy Policy periodically for any changes.")
        }

        LegalSection("11. Contact Us") {
            Text("If you have any questions about this Privacy Policy, please contact us at support@videoscore.io")
        }

        P({
            style {
                marginTop(32.px)
                fontSize(13.px)
                color(Color("#64748b"))
                fontStyle("italic")
            }
        }) {
            Text("Last updated: March 2026")
        }
    }
}

@Composable
fun ContactPage(onBack: (() -> Unit)? = null) {
    Div({
        style {
            minHeight(100.vh)
            padding(24.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
        }
    }) {
        Div({
            style {
                maxWidth(600.px)
                width(100.percent)
            }
        }) {
            // Header with back button
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                    marginBottom(32.px)
                    paddingTop(20.px)
                }
            }) {
                if (onBack != null) {
                    Button({
                        style {
                            backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                            border(1.px, LineStyle.Solid, Color("rgba(255, 255, 255, 0.1)"))
                            borderRadius(8.px)
                            padding(10.px, 16.px)
                            color(Color("#94a3b8"))
                            cursor("pointer")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                            fontSize(14.px)
                        }
                        onClick { onBack() }
                    }) {
                        Text("← Back")
                    }
                }

                H1({
                    style {
                        fontSize(28.px)
                        fontWeight(700)
                        color(Color("#f8fafc"))
                        margin(0.px)
                    }
                }) { Text("Contact Us") }
            }

            // Content card
            Div({
                classes("card")
                style {
                    padding(40.px)
                }
            }) {
                // Contact info
                Div({
                    style {
                        marginBottom(32.px)
                        textAlign("center")
                    }
                }) {
                    P({
                        style {
                            fontSize(15.px)
                            color(Color("#94a3b8"))
                            lineHeight("1.6")
                        }
                    }) {
                        Text("Have questions, feedback, or need help? We'd love to hear from you.")
                    }
                }

                // Contact methods
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                        gap(16.px)
                        marginBottom(32.px)
                    }
                }) {
                    ContactMethodCard(
                        icon = "📧",
                        title = "Email",
                        value = "support@videoscore.io"
                    )
                    ContactMethodCard(
                        icon = "💬",
                        title = "Response Time",
                        value = "Within 24-48 hours"
                    )
                }

                // Email button
                A(href = "mailto:support@videoscore.io") {
                    Button({
                        classes("btn", "btn-primary", "btn-large")
                        style {
                            width(100.percent)
                            property("background", "linear-gradient(135deg, #E53935 0%, #C62828 100%)")
                        }
                    }) {
                        Text("Send us an Email")
                    }
                }
            }

            // FAQ section
            Div({
                style {
                    marginTop(32.px)
                }
            }) {
                H3({
                    style {
                        fontSize(18.px)
                        fontWeight(600)
                        color(Color("#f8fafc"))
                        marginBottom(20.px)
                        textAlign("center")
                    }
                }) { Text("Frequently Asked Questions") }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(12.px)
                    }
                }) {
                    FaqItem(
                        question = "How does VideoScore work?",
                        answer = "Simply paste a YouTube video URL and our AI-powered system analyzes the title, thumbnail, description, tags, and more to provide actionable insights for optimization."
                    )
                    FaqItem(
                        question = "Is there a free trial?",
                        answer = "Yes! You can analyze videos for free when you sign up. No credit card required."
                    )
                    FaqItem(
                        question = "How accurate are the recommendations?",
                        answer = "Our recommendations are based on YouTube best practices and AI analysis of successful videos. While results may vary, users typically see improved engagement after implementing our suggestions."
                    )
                    FaqItem(
                        question = "Can I analyze competitor videos?",
                        answer = "Absolutely! You can analyze any public YouTube video to learn from successful creators in your niche."
                    )
                }
            }
        }
    }
}

@Composable
private fun ContactMethodCard(icon: String, title: String, value: String) {
    Div({
        style {
            backgroundColor(Color("rgba(255, 255, 255, 0.03)"))
            border(1.px, LineStyle.Solid, Color("rgba(255, 255, 255, 0.08)"))
            borderRadius(12.px)
            padding(20.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(28.px)
                marginBottom(8.px)
            }
        }) { Text(icon) }

        Div({
            style {
                fontSize(12.px)
                color(Color("#64748b"))
                property("text-transform", "uppercase")
                letterSpacing(0.5.px)
                marginBottom(4.px)
            }
        }) { Text(title) }

        Div({
            style {
                fontSize(14.px)
                color(Color("#f8fafc"))
                fontWeight(500)
            }
        }) { Text(value) }
    }
}

@Composable
private fun FaqItem(question: String, answer: String) {
    var expanded by remember { mutableStateOf(false) }

    Div({
        classes("card")
        style {
            padding(20.px)
            cursor("pointer")
        }
        onClick { expanded = !expanded }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
            }
        }) {
            Span({
                style {
                    fontSize(14.px)
                    fontWeight(500)
                    color(Color("#f8fafc"))
                }
            }) { Text(question) }

            Span({
                style {
                    fontSize(18.px)
                    color(Color("#64748b"))
                    property("transform", if (expanded) "rotate(180deg)" else "rotate(0deg)")
                    property("transition", "transform 0.2s ease")
                }
            }) { Text("▼") }
        }

        if (expanded) {
            P({
                style {
                    marginTop(12.px)
                    fontSize(14.px)
                    color(Color("#94a3b8"))
                    lineHeight("1.6")
                    margin(12.px, 0.px, 0.px, 0.px)
                }
            }) { Text(answer) }
        }
    }
}
